import { useState, useCallback, useRef } from 'react'
import { imagesApi, ApiError } from '@/lib/images-api'
import type { UploadedImage, ImageUploadProgress } from '@/types/image'
import {
  MAX_IMAGES_PER_UPDATE,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  ALLOWED_IMAGE_TYPES,
} from '@/types/image'

interface ImageUploaderProps {
  onImagesChange: (images: UploadedImage[]) => void
  maxImages?: number
  disabled?: boolean
}

export function ImageUploader({
  onImagesChange,
  maxImages = MAX_IMAGES_PER_UPDATE,
  disabled = false,
}: ImageUploaderProps) {
  const [uploads, setUploads] = useState<ImageUploadProgress[]>([])
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const completedImages = uploads
    .filter((u) => u.status === 'done' && u.image)
    .map((u) => u.image!)

  const canAddMore = completedImages.length < maxImages

  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return `Invalid file type. Allowed: JPEG, PNG, WebP, HEIC`
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `File too large. Maximum ${MAX_FILE_SIZE_MB}MB`
    }
    return null
  }, [])

  const uploadFile = useCallback(
    async (file: File) => {
      // Add to uploads list
      setUploads((prev) => [
        ...prev,
        { file, progress: 0, status: 'pending' },
      ])

      const updateProgress = (progress: number) => {
        setUploads((prev) =>
          prev.map((u) =>
            u.file === file ? { ...u, progress, status: 'uploading' } : u
          )
        )
      }

      try {
        const response = await imagesApi.upload(file, updateProgress)

        setUploads((prev) =>
          prev.map((u) =>
            u.file === file
              ? { ...u, progress: 100, status: 'done', image: response.image }
              : u
          )
        )

        // Notify parent of all completed images
        setUploads((current) => {
          const completed = current
            .filter((u) => u.status === 'done' && u.image)
            .map((u) => u.image!)
          onImagesChange(completed)
          return current
        })
      } catch (err) {
        const errorMessage =
          err instanceof ApiError ? err.message : 'Upload failed'
        setUploads((prev) =>
          prev.map((u) =>
            u.file === file ? { ...u, status: 'error', error: errorMessage } : u
          )
        )
      }
    },
    [onImagesChange]
  )

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files)
      const remainingSlots = maxImages - completedImages.length
      const filesToProcess = fileArray.slice(0, remainingSlots)

      filesToProcess.forEach((file) => {
        const error = validateFile(file)
        if (error) {
          setUploads((prev) => [
            ...prev,
            { file, progress: 0, status: 'error', error },
          ])
          return
        }
        uploadFile(file)
      })
    },
    [maxImages, completedImages.length, validateFile, uploadFile]
  )

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragActive(false)

      if (disabled || !canAddMore) return

      const { files } = e.dataTransfer
      if (files.length > 0) {
        handleFiles(files)
      }
    },
    [disabled, canAddMore, handleFiles]
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target
      if (files && files.length > 0) {
        handleFiles(files)
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleFiles]
  )

  const handleRemove = useCallback(
    (imageId: string) => {
      setUploads((prev) => {
        const updated = prev.filter(
          (u) => !(u.status === 'done' && u.image?.id === imageId)
        )
        const completed = updated
          .filter((u) => u.status === 'done' && u.image)
          .map((u) => u.image!)
        onImagesChange(completed)
        return updated
      })
    },
    [onImagesChange]
  )

  const handleRetry = useCallback(
    (file: File) => {
      setUploads((prev) => prev.filter((u) => u.file !== file))
      uploadFile(file)
    },
    [uploadFile]
  )

  const handleDismissError = useCallback((file: File) => {
    setUploads((prev) => prev.filter((u) => u.file !== file))
  }, [])

  return (
    <div className="space-y-3">
      <label className="mb-1 block text-sm font-medium">
        Photos ({completedImages.length}/{maxImages})
      </label>

      {/* Upload zone */}
      {canAddMore && (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
          className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            isDragActive
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_IMAGE_TYPES.join(',')}
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled}
          />
          <div className="text-gray-500">
            <svg
              className="mx-auto h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="mt-2 text-sm">
              Drag photos here or <span className="text-primary-600">browse</span>
            </p>
            <p className="mt-1 text-xs text-gray-400">
              JPEG, PNG, WebP, HEIC up to {MAX_FILE_SIZE_MB}MB
            </p>
          </div>
        </div>
      )}

      {/* Image previews */}
      {uploads.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
          {uploads.map((upload, idx) => (
            <div
              key={`${upload.file.name}-${idx}`}
              className="relative aspect-square overflow-hidden rounded-lg bg-gray-100"
            >
              {/* Preview image */}
              {upload.status === 'done' && upload.image ? (
                <img
                  src={upload.image.thumbnailUrl}
                  alt="Uploaded"
                  className="h-full w-full object-cover"
                />
              ) : (
                <img
                  src={URL.createObjectURL(upload.file)}
                  alt="Preview"
                  className="h-full w-full object-cover"
                />
              )}

              {/* Uploading overlay */}
              {(upload.status === 'pending' || upload.status === 'uploading') && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="text-center text-white">
                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    <span className="mt-1 block text-xs">{upload.progress}%</span>
                  </div>
                </div>
              )}

              {/* Error overlay */}
              {upload.status === 'error' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-500 bg-opacity-80 p-2">
                  <span className="text-center text-xs text-white">
                    {upload.error}
                  </span>
                  <div className="mt-2 flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleRetry(upload.file)}
                      className="rounded bg-white px-2 py-1 text-xs text-red-600"
                    >
                      Retry
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDismissError(upload.file)}
                      className="rounded bg-white px-2 py-1 text-xs text-gray-600"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}

              {/* Remove button for completed uploads */}
              {upload.status === 'done' && upload.image && (
                <button
                  type="button"
                  onClick={() => handleRemove(upload.image!.id)}
                  className="absolute right-1 top-1 rounded-full bg-black bg-opacity-60 p-1 text-white hover:bg-opacity-80"
                  disabled={disabled}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
